import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaPaperPlane,
  FaRobot,
  FaUser,
  FaComments,
  FaTimes,
  FaFlag,
} from 'react-icons/fa';
import ApiStore from '../../api/ApiStore';
import AppStore from '../../appStore';
import ReactMarkdownRenderer from 'react-markdown-renderer';
import './ChatWindow.css';
import ReportModal from './ReportModal';

const ChatWindow = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState('ai');
  const [selectedInstructorId, setSelectedInstructorId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportMessageId, setReportMessageId] = useState(null);

  const pollIntervalRef = useRef(null);
  const lastMessageIdRef = useRef(lastMessageId);
  const messagesEndRef = useRef(null);

  // Para controlar el "badgeCount" inicial cuando no hay mensajes
  const [hasInteracted, setHasInteracted] = useState(false);

  const organizationName =
    AppStore.organizationDetails?.OrganizationName || 'DefaultOrg';

  // Actualiza la ref cuando lastMessageId cambie
  useEffect(() => {
    lastMessageIdRef.current = lastMessageId;
  }, [lastMessageId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchAllMessages = useCallback(async () => {
    setChatLoading(true);
    try {
      const all = await ApiStore.getChatMessages(organizationName, null);
      setMessages(all);
      if (all.length > 0) {
        setLastMessageId(all[all.length - 1].message_id);
      } else {
        setLastMessageId(0);
      }
    } catch (err) {
      console.error('Error fetching all chat messages:', err);
    } finally {
      setChatLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  }, [organizationName]);

  const fetchNewMessages = useCallback(async () => {
    const currentLastId = lastMessageIdRef.current;
    if (currentLastId === 0) return;

    try {
      const newMsgs = await ApiStore.getChatMessages(
        organizationName,
        currentLastId
      );
      if (newMsgs.length > 0) {
        setMessages(prev => [...prev, ...newMsgs]);
        setLastMessageId(newMsgs[newMsgs.length - 1].message_id);

        // Borra los mensajes "pendientes" ya reemplazados por respuesta del servidor
        setPendingMessages(prevPending =>
          prevPending.filter(
            p =>
              !newMsgs.some(
                nm =>
                  nm.message_text === p.message_text &&
                  nm.sender_type === p.sender_type
              )
          )
        );

        // Si el chat está contraído, actualiza el conteo "unreadCount" para los mensajes de otros
        if (!isExpanded) {
          const newFromOthers = newMsgs.filter(
            m => m.sender_type !== 'user'
          ).length;
          if (newFromOthers > 0) {
            setUnreadCount(prev => prev + newFromOthers);
          }
        } else {
          setTimeout(scrollToBottom, 100);
        }
      }
    } catch (err) {
      console.error('Error fetching new messages:', err);
    }
  }, [organizationName, isExpanded]);

  const setupPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    const intervalMs = isExpanded ? 20000 : 60000;
    pollIntervalRef.current = setInterval(() => {
      if (lastMessageIdRef.current > 0) {
        fetchNewMessages();
      } else {
        fetchAllMessages();
      }
    }, intervalMs);
  }, [isExpanded, fetchAllMessages, fetchNewMessages]);

  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (isExpanded) {
      fetchAllMessages().then(() => {
        // Resetea el conteo de "unread" cuando se abre el chat
        setUnreadCount(0);
      });
    } else {
      if (lastMessageIdRef.current > 0) {
        fetchNewMessages();
      } else {
        fetchAllMessages();
      }
    }

    setupPolling();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isExpanded, setupPolling, fetchAllMessages, fetchNewMessages]);

  // Cargar lista de instructores si seleccionamos "instructor"
  useEffect(() => {
    if (selectedRecipient === 'instructor') {
      (async () => {
        setInstructorsLoading(true);
        try {
          const data = await ApiStore.getInstructors(organizationName);
          setInstructors(data);
        } catch (error) {
          console.error('Error loading instructors:', error);
        } finally {
          setInstructorsLoading(false);
        }
      })();
    }
  }, [selectedRecipient, organizationName]);

  const handleReportSubmit = async reportMessage => {
    try {
      console.log('Report Message ID:', reportMessageId);

      if (!reportMessageId) {
        console.error('No message ID found for report');
        return;
      }

      const fullReportText = `${reportMessage} [Mensaje de IA: ${reportMessageId}]`;

      const localMsg = {
        message_id: Date.now(),
        sender_type: 'user',
        message_text: fullReportText,
        timestamp: new Date().toISOString(),
      };

      setPendingMessages(prev => [...prev, localMsg]);

      await ApiStore.postChatMessage(
        organizationName,
        'instructor',
        fullReportText
      );

      setReportMessageId(null);
      setIsReportModalOpen(false);

      fetchNewMessages();
    } catch (err) {
      console.error('Error sending report:', err);
    }
  };

  // Unifica mensajes del servidor con pendientes y ordena por timestamp
  const allMessages = [...messages, ...pendingMessages].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const tempId = Date.now();
    const localMsg = {
      message_id: tempId,
      sender_type: 'user',
      message_text: newMessage,
      timestamp: new Date().toISOString(),
    };

    setPendingMessages(prev => [...prev, localMsg]);
    setNewMessage('');

    try {
      if (selectedRecipient === 'instructor') {
        await ApiStore.postChatMessage(
          organizationName,
          'instructor',
          localMsg.message_text,
          selectedInstructorId
        );
      } else {
        await ApiStore.postChatMessage(
          organizationName,
          'ai',
          localMsg.message_text
        );
      }
      fetchNewMessages();
    } catch (err) {
      console.error('Error sending chat message:', err);
      // En caso de error, removemos el mensaje temporal
      setPendingMessages(prev => prev.filter(m => m.message_id !== tempId));
    }
  };

  const getSenderIcon = sender => {
    switch (sender) {
      case 'ai':
        return <FaRobot className="w-6 h-6 text-blue-400" />;
      case 'instructor':
        return <FaUser className="w-6 h-6 text-green-400" />;
      case 'user':
      default:
        return <FaUser className="w-6 h-6 text-gray-400" />;
    }
  };

  const getMessageStyle = sender => {
    switch (sender) {
      case 'ai':
        return {
          bubble: 'bg-blue-200 text-black rounded-br-none',
          timestamp: 'text-gray-500',
        };
      case 'instructor':
        return {
          bubble: 'bg-green-200 text-black rounded-br-none',
          timestamp: 'text-gray-800',
        };
      case 'user':
      default:
        return {
          bubble: 'bg-gray-100 text-gray-800 rounded-bl-none',
          timestamp: 'text-gray-500',
        };
    }
  };

  const getButtonColor = recipient => {
    return recipient === 'ai'
      ? 'bg-blue-400 hover:bg-blue-500'
      : 'bg-green-400 hover:bg-green-500';
  };

  const formatTimestamp = timestamp => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const days = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    const dayName = days[date.getDay()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${dayName} ${hours}:${minutes}`;
  };

  const toggleChat = () => {
    setIsExpanded(!isExpanded);
    setHasInteracted(true);
    // Si abrimos el chat y está vacío, forzamos unreadCount a 0
    if (!isExpanded && allMessages.length === 0) {
      setUnreadCount(0);
    }
  };

  const getHeaderStyle = recipient => {
    return recipient === 'ai'
      ? { bg: 'bg-blue-400', text: 'text-white' }
      : { bg: 'bg-green-400', text: 'text-white' };
  };

  // Si el chat está contraído, no hay mensajes y el usuario no ha interactuado antes, badge=1
  // En caso contrario, usamos unreadCount
  const badgeCount =
    !isExpanded && allMessages.length === 0 && !hasInteracted ? 1 : unreadCount;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {!isExpanded ? (
        <button
          onClick={toggleChat}
          className="bg-gray-600 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-all duration-200 relative"
        >
          <FaComments className="w-6 h-6" />
          {badgeCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full px-1">
              {badgeCount}
            </span>
          )}
        </button>
      ) : (
        <div
          className="flex flex-col w-96 h-[80vh] bg-gray-50 shadow-xl rounded-lg"
          style={{ maxHeight: '90vh' }}
        >
          <div
            className={`flex justify-between items-center p-2 ${
              getHeaderStyle(selectedRecipient).bg
            } ${getHeaderStyle(selectedRecipient).text} rounded-t-lg transition-colors duration-200`}
          >
            <h2 className="font-medium opacity-75">
              Chat con {selectedRecipient === 'ai' ? 'Agente IA' : 'Instructor'}
            </h2>
            <button
              onClick={toggleChat}
              className="hover:opacity-75 transition-colors"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* Zona de los mensajes */}
          <div
            className="flex-1 p-4 overflow-y-auto overscroll-contain"
            style={{
              height: 'calc(80vh - 180px)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {chatLoading ? (
              <p className="text-gray-500">Cargando mensajes...</p>
            ) : (
              <>
                {allMessages.length === 0 ? (
                  // Mensaje de bienvenida en Markdown si no hay mensajes
                  <div className="flex justify-start mb-4">
                    <div className="mr-2 flex items-start mt-1">
                      {getSenderIcon('ai')}
                    </div>
                    <div
                      className={`max-w-[70%] px-4 py-2 rounded-lg relative ${
                        getMessageStyle('ai').bubble
                      }`}
                    >
                      <div className="text-sm markdown-content">
                        <ReactMarkdownRenderer
                          markdown={`
## 👋 ¡Bienvenid@!

Soy un **Agente de Inteligencia Artificial** diseñado para ayudarte a comprender la normativa del **permiso B**.  

## ¿Tienes una duda?

**1. Selecciona** a quién quieres escribir:
- 🔵 **Agente IA** (🤖): Recibirás una respuesta automática basada en la normativa DGT.  
- 🟢 **Instructor** (👤): Un experto humano responderá tu consulta.

**2. Escribe** tu mensaje y **dale a enviar**  
  
  
⚠️ **Importante**:  Estoy aprendiendo y **puedo cometer errores**.  De modo que si necesitas una respuesta más precisa, ¡no dudes en preguntar a un instructor humano!
                          `}
                          options={{ breaks: true, gfm: true }}
                          className="prose prose-sm prose-invert max-w-none"
                        />
                      </div>
                      <p
                        className={`text-xs mt-1 ${
                          getMessageStyle('ai').timestamp
                        }`}
                      >
                        {formatTimestamp(new Date().toISOString())}
                      </p>
                    </div>
                  </div>
                ) : (
                  // Renderizar los mensajes existentes
                  allMessages.map(message => (
                    <div
                      key={message.message_id}
                      className={`flex ${
                        message.sender_type === 'user'
                          ? 'justify-end'
                          : 'justify-start'
                      } mb-4`}
                    >
                      {/* Icono del emisor */}
                      {message.sender_type !== 'user' && (
                        <div className="mr-2 flex items-start mt-1">
                          {getSenderIcon(message.sender_type)}
                        </div>
                      )}
                      {/* Burbuja de mensaje */}
                      <div
                        className={`max-w-[70%] px-4 py-2 rounded-lg relative ${
                          getMessageStyle(message.sender_type).bubble
                        }`}
                      >
                        {message.sender_type === 'ai' ? (
                          <div className="text-sm markdown-content relative group">
                            <ReactMarkdownRenderer
                              markdown={message.message_text}
                              options={{ breaks: true, gfm: true }}
                              className="prose prose-sm prose-invert max-w-none"
                            />
                            <div className="flex justify-between items-center mt-1">
                              <p
                                className={`text-xs ${
                                  getMessageStyle(message.sender_type).timestamp
                                }`}
                              >
                                {formatTimestamp(message.timestamp)}
                              </p>
                              <button
                                onClick={() => {
                                  setReportMessageId(message.message_id);
                                  setIsReportModalOpen(true);
                                }}
                                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <FaFlag className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm">{message.message_text}</p>
                            <p
                              className={`text-xs mt-1 ${
                                getMessageStyle(message.sender_type).timestamp
                              }`}
                            >
                              {formatTimestamp(message.timestamp)}
                            </p>
                          </>
                        )}
                      </div>
                      {/* Icono del usuario */}
                      {message.sender_type === 'user' && (
                        <div className="ml-2 flex items-start mt-1">
                          {getSenderIcon(message.sender_type)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Barra inferior: cambio de destinatario y entrada de texto */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <button
                onClick={() => {
                  setSelectedRecipient('ai');
                  setSelectedInstructorId(null);
                }}
                className={`flex items-center text-sm space-x-2 rounded-full transition-all duration-200 ${
                  selectedRecipient === 'ai'
                    ? 'bg-gray-100 text-blue-500 px-3 py-1.5'
                    : 'bg-gray-50 text-gray-500 px-3 py-1.5'
                }`}
              >
                <FaRobot className="w-4 h-4" />
                <span
                  className={selectedRecipient === 'instructor' ? 'hidden' : ''}
                >
                  Agente IA
                </span>
              </button>

              <div className="flex items-center text-sm space-x-2">
                <button
                  onClick={() => setSelectedRecipient('instructor')}
                  className={`flex items-center space-x-1 ${
                    selectedRecipient === 'instructor'
                      ? 'bg-gray-100 text-green-500 px-3 py-1.5'
                      : 'bg-gray-50 text-gray-500 px-3 py-1.5'
                  } rounded-full transition-all duration-200`}
                >
                  <FaUser className="w-4 h-4" />
                  <span
                    className={
                      selectedRecipient === 'instructor' ? 'text-sm' : ''
                    }
                  >
                    Instructor
                  </span>
                </button>

                {selectedRecipient === 'instructor' && (
                  <select
                    disabled={instructorsLoading}
                    className="border border-gray-200 rounded-full py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                    value={selectedInstructorId || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setSelectedInstructorId(
                        val === '' ? null : parseInt(val)
                      );
                    }}
                  >
                    <option value="">(Cualquiera)</option>
                    {instructors.map(instr => (
                      <option key={instr.user_id} value={instr.user_id}>
                        {instr.user_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {selectedRecipient === 'ai' && (
              <div className="text-gray-500 text-xs text-center mb-4 italic animate-fade-in">
                Nuestra IA está aprendiendo y puede equivocarse.
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex space-x-2">
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                placeholder={`Mensaje para ${
                  selectedRecipient === 'ai' ? 'Agente IA' : 'Instructor'
                }...`}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              />
              <button
                type="submit"
                className={`px-4 py-2 ${getButtonColor(
                  selectedRecipient
                )} text-white rounded-full focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200`}
              >
                <FaPaperPlane className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal para reporte de mensajes IA */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setReportMessageId(null);
        }}
        onSubmit={handleReportSubmit}
        messageId={reportMessageId}
      />
    </div>
  );
};

export default ChatWindow;
